import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { adminFetch, apiErrorMessage, errorMessage } from './adminFetch';

/**
 * Back-office › Espaces — fermer un espace ou un dossier, et la purge du
 * portail à l'échéance (01/11/2026).
 *
 * Fermer est IRRÉVERSIBLE depuis l'écran : les produits relisent les
 * fermetures chaque nuit et lancent leurs durées de conservation. D'où le
 * motif obligatoire et le nom à retaper.
 */

const JOUR = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' });
const date = (iso: string | null) => (iso ? JOUR.format(new Date(iso)) : '—');

interface Apercu {
  id: string;
  nom: string;
  fermeLe: string | null;
  dossiersOuverts: { id: string; name: string }[];
  membres: { email: string; role: 'OWNER' | 'ADMIN' | 'MEMBER'; nouvelEspace: boolean }[];
  abonnementsVivants: number;
  regle: 'SANS_TRACE' | 'CONSERVATION';
  purgePrevueLe: string;
}

interface Echeance {
  spaceId: string;
  nom: string;
  fermeLe: string;
  regle: 'SANS_TRACE' | 'CONSERVATION';
  purgePrevueLe: string;
  echue: boolean;
}

const REGLES = {
  SANS_TRACE: 'sans trace — purge 30 jours après la fermeture',
  CONSERVATION: 'factures ou données — purge dix ans après la fermeture',
} as const;

async function envoyer(chemin: string, corps?: unknown) {
  const res = await adminFetch(chemin, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: corps === undefined ? undefined : JSON.stringify(corps),
  });
  if (!res.ok) throw new Error(await apiErrorMessage(res));
  return res.json();
}

/** Motif + nom retapé : le formulaire commun aux deux fermetures. */
function FormulaireFermeture({
  nom,
  libelle,
  onConfirmer,
  onAnnuler,
}: {
  nom: string;
  libelle: string;
  onConfirmer: (motif: string, confirmation: string) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [motif, setMotif] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const pret = motif.trim().length >= 3 && confirmation.trim().toLowerCase() === nom.trim().toLowerCase();

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      await onConfirmer(motif, confirmation);
    } catch (err) {
      setErreur(errorMessage(err));
      setEnvoi(false);
    }
  }

  return (
    <form className="adm-esp-fermeture-form" onSubmit={(e) => void soumettre(e)}>
      <label>
        Motif (journalisé)
        <textarea value={motif} maxLength={500} rows={2} onChange={(e) => setMotif(e.target.value)} required />
      </label>
      <label>
        Pour confirmer, saisissez « {nom} »
        <input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off" />
      </label>
      {erreur && (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      )}
      <div className="adm-esp-fermeture-actions">
        <button className="btn btn-secondary btn-small" type="button" onClick={onAnnuler} disabled={envoi}>
          Annuler
        </button>
        <button className="btn btn-danger btn-small" type="submit" disabled={!pret || envoi}>
          {envoi ? 'Fermeture…' : libelle}
        </button>
      </div>
    </form>
  );
}

/** Dans le détail d'un espace ouvert : « Fermer l'espace… ». */
export function FermerEspace({ spaceId, onFait }: { spaceId: string; onFait: () => void }) {
  const [apercu, setApercu] = useState<Apercu | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function ouvrir() {
    setOuvert(true);
    setErreur(null);
    try {
      const res = await adminFetch(`/admin/fermetures/espaces/${spaceId}`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setApercu((await res.json()) as Apercu);
    } catch (err) {
      setErreur(errorMessage(err));
    }
  }

  if (!ouvert) {
    return (
      <button className="btn btn-danger btn-small" type="button" onClick={() => void ouvrir()}>
        Fermer l’espace…
      </button>
    );
  }
  return (
    <section className="adm-esp-fermeture" aria-labelledby={`fermer-${spaceId}`}>
      <h3 id={`fermer-${spaceId}`}>Fermer l’espace</h3>
      {erreur && (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      )}
      {!apercu && !erreur && <p role="status">Chargement…</p>}
      {apercu && (
        <>
          <ul className="adm-esp-liste">
            <li>
              {apercu.dossiersOuverts.length} dossier(s) fermé(s) pour toute la suite
              {apercu.dossiersOuverts.length ? ` : ${apercu.dossiersOuverts.map((d) => d.name).join(', ')}` : ''}.
            </li>
            <li>
              {apercu.membres.length} membre(s) retiré(s), invitations révoquées.
              {apercu.membres.some((m) => m.nouvelEspace) && (
                <>
                  {' '}
                  Recevront un espace neuf et vide (ils n’en ont pas d’autre) :{' '}
                  {apercu.membres
                    .filter((m) => m.nouvelEspace)
                    .map((m) => m.email)
                    .join(', ')}
                  .
                </>
              )}
            </li>
            <li>
              {apercu.abonnementsVivants
                ? `Abonnement résilié à l’échéance (${apercu.abonnementsVivants} ligne(s)) — la période payée n’est pas remboursée : le faire dans Stripe si besoin.`
                : 'Aucun abonnement en cours.'}
            </li>
            <li>
              Purge au portail : {REGLES[apercu.regle]}, soit le {date(apercu.purgePrevueLe)} si l’espace est fermé
              aujourd’hui. Chaque produit applique en plus ses propres durées.
            </li>
          </ul>
          <FormulaireFermeture
            nom={apercu.nom}
            libelle="Fermer définitivement l’espace"
            onAnnuler={() => setOuvert(false)}
            onConfirmer={async (motif, confirmation) => {
              await envoyer(`/admin/fermetures/espaces/${spaceId}`, { motif, confirmation });
              onFait();
            }}
          />
        </>
      )}
    </section>
  );
}

/** Dans la liste des dossiers : « Fermer ce dossier… ». */
export function FermerDossier({ companyId, nom, onFait }: { companyId: string; nom: string; onFait: () => void }) {
  const [ouvert, setOuvert] = useState(false);
  if (!ouvert) {
    return (
      <button className="btn btn-link btn-small" type="button" onClick={() => setOuvert(true)}>
        Fermer ce dossier…
      </button>
    );
  }
  return (
    <div className="adm-esp-fermeture">
      <p className="adm-muted">
        Le dossier est fermé pour toute la suite ; si c’est le dernier de l’espace, l’abonnement est résilié à
        l’échéance.
      </p>
      <FormulaireFermeture
        nom={nom}
        libelle="Fermer définitivement le dossier"
        onAnnuler={() => setOuvert(false)}
        onConfirmer={async (motif, confirmation) => {
          await envoyer(`/admin/fermetures/dossiers/${companyId}`, { motif, confirmation });
          onFait();
        }}
      />
    </div>
  );
}

/** Sous les statistiques : l'échéancier des purges et les comptes du test OIDC. */
export function PanneauPurges({ onFait }: { onFait: () => void }) {
  const [donnees, setDonnees] = useState<{ echeances: Echeance[]; echus: number; comptesSmoke: number } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const res = await adminFetch('/admin/fermetures/purges');
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setDonnees(await res.json());
    } catch (err) {
      setErreur(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    // Chargement initial de l'échéancier.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void charger();
  }, [charger]);

  async function agir(chemin: string, texte: (r: Record<string, number>) => string) {
    setEnvoi(true);
    setErreur(null);
    setMessage(null);
    try {
      setMessage(texte(await envoyer(chemin)));
      await charger();
      onFait();
    } catch (err) {
      setErreur(errorMessage(err));
    } finally {
      setEnvoi(false);
    }
  }

  const nbEchus = donnees?.echus ?? 0;
  const prochaines = donnees?.echeances.filter((e) => !e.echue).slice(0, 5) ?? [];

  return (
    <section className="adm-esp-carte adm-esp-purges" aria-labelledby="esp-purges">
      <h2 id="esp-purges">Fermetures et purge</h2>
      <p className="adm-muted">
        Un espace fermé est purgé du portail 30 jours après sa fermeture s’il n’a laissé aucune trace, dix ans après
        sinon (factures, dossiers ouverts dans un produit, GED). L’entretien horaire le fait seul.
      </p>
      {erreur && (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      )}
      {message && <p role="status">{message}</p>}
      {!donnees ? (
        <p role="status">Chargement…</p>
      ) : (
        <>
          <p>
            {nbEchus} espace(s) fermé(s) à purger maintenant. Prochaines échéances (espaces fermés récemment) :
          </p>
          {prochaines.length > 0 && (
            <ul className="adm-esp-liste">
              {prochaines.map((e) => (
                <li key={e.spaceId}>
                  <strong>{e.nom}</strong>{' '}
                  <span className="adm-muted">
                    fermé le {date(e.fermeLe)} · purge le {date(e.purgePrevueLe)} ·{' '}
                    {e.regle === 'SANS_TRACE' ? 'sans trace' : 'conservation'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="adm-esp-fermeture-actions">
            <button
              className="btn btn-secondary btn-small"
              type="button"
              disabled={envoi || nbEchus === 0}
              onClick={() => void agir('/admin/fermetures/purges', (r) => `${r.purges} espace(s) purgé(s), ${r.echecs} en échec.`)}
            >
              Purger les échus maintenant ({nbEchus})
            </button>
            <button
              className="btn btn-secondary btn-small"
              type="button"
              disabled={envoi || donnees.comptesSmoke === 0}
              onClick={() =>
                void agir('/admin/fermetures/comptes-smoke', (r) => `${r.supprimes} compte(s) de test supprimé(s).`)
              }
            >
              Supprimer les comptes du test OIDC ({donnees.comptesSmoke})
            </button>
          </div>
        </>
      )}
    </section>
  );
}
